<script setup>
import { inject, computed } from 'vue';
import { ChevronRight } from '@lucide/vue';
import useCartStore from '../store/cart';
import LineItems from './LineItems.vue';
import OrderTotal from './OrderTotal.vue';
import OrderTypeToggle from './OrderTypeToggle.vue';
import ModalHead from './ModalHead.vue';
import ModalFooter from './ModalFooter.vue';
import ModalBtn from './ModalBtn.vue';

const rootData = inject('rootData');

const availableToContinue = computed(() => Boolean(useCartStore()?.count));
const nextStepLink = computed(() => {
    if (rootData.ordering.flow == 'one') {
        return { name: 'checkout' };
    } else if (rootData.ordering.flow == 'default') {
        return { name: 'customer' };
    }
})
const nextStepLabel = computed(() => {
    if (useCartStore().count <= 0) return 'Cart is empty';
    if (rootData.ordering.flow == 'one') {
        return 'Checkout';
    } else if (rootData.ordering.flow == 'default') {
        return 'Customer';
    }
})
</script>
<template>
    <ModalHead>Your Order</ModalHead>
    <div class="modal__body">
        <OrderTypeToggle />
        <LineItems />
        <OrderTotal />
    </div>
    <ModalFooter>
        <template v-if="availableToContinue">
            <router-link :to="nextStepLink" custom v-slot="{ navigate }">
                <ModalBtn @click="navigate" role="link" class="btn-primary" :disabled="Boolean(!(useCartStore()?.count))">
                    {{ nextStepLabel }}
                    <ChevronRight />
                </ModalBtn>
            </router-link>
        </template>
        <template v-else>
            <ModalBtn disabled class="btn-inactive">{{ nextStepLabel }}</ModalBtn>
        </template>
    </ModalFooter>
</template>

<style lang="scss" scoped>
.cart-total {
    margin: auto -1rem -1rem;
}

.modal {
    &__body {
        padding: 16px;
        min-height: auto;
        display: flex;
        flex-direction: column;
    }
}

.action-remove {
    z-index: 2;
    width: 1.25rem;
    height: 1.25rem;
    overflow: hidden;
    outline: none;
    padding: 0;
    line-height: 1;
    position: relative;
    cursor: pointer;

    &::before,
    &::after {
        content: '';
        display: block;
        width: 75%;
        height: 1px;
        background-color: #828282;
        top: 50%;
        left: 50%;
        transform-origin: center;
        position: absolute;
    }

    &::before {
        border-radius: 2px 0 0 2px;
        transform: translate3d(-50%, -50%, 0) rotate(-45deg);
    }

    &::after {
        border-radius: 0 2px 2px 0;
        transform: translate3d(-50%, -50%, 0) rotate(45deg);
    }

    &:hover {

        &::before,
        &::after {
            background-color: red;
        }
    }
}

.line-item {
    &:not(:last-child) {
        border-bottom: 1px solid #d5d5d5;
    }

    &__inner {
        min-height: 48px;
        margin: 1em .4em;
    }

    &__desc {
        align-items: center;
        color: #373737;
        display: flex;
        font-size: 1rem;
        margin: 0;
    }

    &__name {
        width: 65%;
        font-weight: 400;
    }

    &__price {
        margin: 0;
        text-align: right;
        width: 35%;
        font-weight: 500;
        font-size: 1rem;
        font-size: 0.875em;
        display: flex;
        justify-content: flex-end;

        div {
            position: relative;
            padding-left: .375rem;
            padding-right: .375rem;

            &:first-child {
                padding-left: 0;

                &:not(:last-child) {
                    padding-right: .375rem;

                    &::after {
                        content: "";
                        width: 1px;
                        height: 100%;
                        background-color: currentColor;
                        position: absolute;
                        opacity: 0.75;
                        right: 0;
                        transform-origin: center;
                        transform: translate3d(-50%, -1px, 0);
                        top: 0;
                    }
                }
            }

        }

    }

    &__count {
        font-weight: 300;
        font-size: 0.875em;
    }

    &__container {
        display: flex;
        flex-direction: row;
        margin-top: .2em;
    }

    &__mods {
        color: #727272;
        flex-grow: 1;
        margin: 0 60px 0 0;
        font-size: 0.875rem;
    }

    // &__actions {
    //     align-items: center;
    //     background: transparent;
    //     border: 0;
    //     cursor: pointer;
    //     height: 28px;
    //     justify-content: flex-end;
    //     width: 28px;
    // }
}
</style>